// ============================================================================
// ADMIN AUTHENTICATION SERVICE
// ============================================================================

const API_BASE_URL = 'https://api.fsgu.co.za';

/**
 * Verify Firebase ID token and check admin status
 * @param {string} idToken - Firebase ID token
 * @param {string} email - User email
 * @returns {Promise<Object|null>} Admin data or null if not admin
 */
export const verifyAdminToken = async (idToken, email) => {
  try {
    const response = await fetch(`${API_BASE_URL}/admin-verify.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        idToken,
        email
      })
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Admin verification failed');
    }

    return data.admin;
  } catch (error) {
    console.error('Admin verification error:', error);
    throw error;
  }
};

/**
 * Get current admin data from session
 * @returns {Object|null} Admin data or null
 */
export const getAdminData = () => {
  try {
    const data = sessionStorage.getItem('adminData');
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error getting admin data:', error);
    return null;
  }
};

/**
 * Check if current user has a specific role
 * @param {string} requiredRole - Required role
 * @returns {boolean} True if user has role
 */
export const hasRole = (requiredRole) => {
  const adminData = getAdminData();
  if (!adminData) return false;

  const roleHierarchy = {
    'dev': 4,          // Developer - highest level, untouchable
    'super_admin': 3,
    'admin': 2,
    'editor': 1
  };

  const userRoleLevel = roleHierarchy[adminData.role] || 0;
  const requiredRoleLevel = roleHierarchy[requiredRole] || 0;

  return userRoleLevel >= requiredRoleLevel;
};

/**
 * Check if current user is dev
 * @returns {boolean} True if user is dev
 */
export const isDev = () => {
  const adminData = getAdminData();
  return adminData?.role === 'dev';
};

/**
 * Clear admin session data
 */
export const clearAdminSession = () => {
  sessionStorage.removeItem('adminData');
};